/**
 * The pages step 1 builds in full — sign-in, tenant links, Not found — and the "Coming soon"
 * placeholder every other address renders until its build step lands.
 */
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthGate, useAuth } from '@inferrex/ui/auth';
import { captureTenantLink, StateBlock } from '@inferrex/ui';
import { comingSoonMessage, type AppRoute } from '../routes';

/**
 * Sign-in: the generated gate, the same behaviour as today — hosted installs go straight to the
 * identity provider (Entra); self-hosted ones get email and password, then the 6-digit code when
 * two-factor is on. /login/workspace shows the workspace-ID box. Signed in already → home.
 */
export function SignInPage() {
  const { user, loading, configError } = useAuth();
  const { pathname } = useLocation();
  if (!loading && user && !configError && pathname !== '/login/workspace') return <Navigate to="/" replace />;
  return <AuthGate />;
}

/** /t/<workspace id>: remember the named workspace for this session, then go home to enter it. */
export function TenantLinkPage() {
  const { pathname } = useLocation();
  useState(() => captureTenantLink(pathname));
  return <Navigate to="/" replace />;
}

/** A page that is not built yet. Says so plainly; never invents content. */
export function ComingSoonPage({ route }: { route: AppRoute }) {
  return (
    <div className="app-page app-page--center">
      <div className="app-card">
        <StateBlock kind="empty" title="Coming soon" message={comingSoonMessage(route)} />
      </div>
    </div>
  );
}

/** The same, outside the shell (sign-in callbacks, invite landing). */
export function FrameComingSoonPage({ route }: { route: AppRoute }) {
  return (
    <div className="app-frame">
      <div className="app-card">
        <StateBlock kind="empty" title="Coming soon" message={comingSoonMessage(route)} primary={{ label: 'Go to Home', href: '/' }} />
      </div>
    </div>
  );
}

/** Any address no route claims (PAGES.md "Not found"). */
export function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="app-page app-page--center">
      <div className="app-card">
        <StateBlock
          kind="empty"
          title="This page doesn’t exist"
          message="The address may be mistyped, or the page has moved."
          primary={{ label: 'Go to Home', onClick: () => navigate('/') }}
        />
      </div>
    </div>
  );
}
